import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { SurveyModal } from '../components/SurveyModal';
import { surveysApi } from '../surveyApi';
import type { Survey } from '../surveyTypes';
import { describeTargets } from '../utils/describeTargets';

/**
 * Learning & Development › Survey — the survey register.
 *
 * The standard management flow: index, create, profile, edit, delete. The anchor opens the
 * **profile**, not the edit form — every index page in this product is partnered with one.
 */
export function SurveysIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<Survey | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    survey: Survey;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchSurveys = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      surveysApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (survey: Survey): MenuItem[] => [
      {
        key: `edit-${survey.id}`,
        label: 'Edit',
        permission: 'surveys.update',
        onClick: () => setEditing(survey),
      },
      {
        key: `restore-${survey.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.delete',
        disabledReason: survey.isActive ? 'This survey is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', survey }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await surveysApi.deactivate(confirming.survey.id);
      } else {
        await surveysApi.activate(confirming.survey.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<Survey>
        module="surveys"
        breadcrumb={['Learning & Development', 'Survey']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        // The links and questionnaires are written under the survey, so its history lists them too.
        auditEntityTypes={['Survey', 'SurveyOrgUnit', 'SurveyPosition', 'SurveyQuestionnaire']}
        rowKey={(survey) => survey.id}
        fetch={fetchSurveys}
        reloadKey={reloadKey}
        searchPlaceholder="Search surveys"
        emptyMessage="No surveys yet."
        onCreate={() => setCreating(true)}
        createPermission="surveys.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(survey) => navigate(`/learning-development/survey/${survey.id}`)}
        openPermission="surveys.read"
        rowActions={rowActions}
        onDelete={(survey) => setConfirming({ kind: 'deactivate', survey })}
        deletePermission="surveys.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(survey) => (survey.isActive ? null : 'This survey is already deleted.')}
        onClearData={() => surveysApi.clear()}
        clearDataPermission="surveys.clear-data"
        otherInfo={(survey) => survey}
      />

      {creating || editing !== null ? (
        <SurveyModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete survey' : 'Restore survey'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`survey-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.survey.title}" is deleted.`
                  : `"${confirming.survey.title}" is restored.`}
              </p>

              <p>Nothing is erased — a deleted survey can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<Survey>[] = [
  {
    // The title is the anchor. The row number is the shell's own `#` gutter, not a column.
    key: 'title',
    header: 'Title',
    width: 360,
    flex: true,
    sortable: true,
    render: (survey) => <TextCell value={survey.title} />,
  },
  {
    /*
      Division and position in ONE column, by product call — two SETS joined on one line, never
      stacked. An empty set reads "All divisions" / "All positions", because that is what it
      means. Not sortable (a set has no order a header could promise); the search covers both.
    */
    key: 'divisionPosition',
    header: 'Units/Organization / Position',
    width: 360,
    summary: true,
    render: (survey) => (
      <TextCell
        value={`${describeTargets(survey.orgUnits, 'All units/organizations')} — ${describeTargets(survey.positions, 'All positions')}`}
      />
    ),
  },
  {
    // Counted live by the server — a zero is muted, not a dash: the value is none, not absent.
    // Not sortable: a correlated count would be computed for every survey before paging.
    key: 'respondentCount',
    header: 'No. of Respondents',
    width: 150,
    align: 'right',
    summary: true,
    render: (survey) => <TextCell value={String(survey.respondentCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (survey) =>
      survey.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
